import React from 'react';
import { Link } from 'react-router-dom';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-light' | 'white';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  href, 
  fullWidth = false,
  className = '',
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center text-center font-bold tracking-wide rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] transition-all duration-300 ease-out hover:-translate-y-0.5";
  
  const variants = {
    primary: "bg-orange-600 hover:bg-orange-700 text-white focus:ring-orange-500 border border-transparent shadow-sm btn-glow-orange",
    secondary: "bg-slate-800 hover:bg-slate-900 text-white focus:ring-slate-500 border border-transparent shadow-sm hover:shadow-lg",
    outline: "bg-transparent hover:bg-slate-900 hover:text-white text-slate-900 border-2 border-slate-900 focus:ring-slate-500",
    'outline-light': "bg-transparent hover:bg-white/10 text-white border-2 border-white/60 hover:border-white focus:ring-white btn-fill-hover",
    white: "bg-white hover:bg-slate-50 text-slate-900 focus:ring-white border border-slate-200 shadow-sm hover:shadow-md",
  };

  const sizes = {
    sm: "px-4 py-2 text-xs",
    md: "px-6 py-3 text-sm",
    lg: "px-8 py-3.5 text-base",
  };

  const widthStyles = fullWidth ? "w-full" : "";
  
  const combinedClassName = `${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyles} ${className}`;

  // Use native <a> for file downloads and external links
  if (href && (href.startsWith('/files/') || href.startsWith('http'))) {
    return (
      <a 
        href={href} 
        className={combinedClassName}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        download={href.startsWith('/files/') ? true : undefined}
      >
        {children}
      </a>
    );
  }

  if (href) {
    return (
      <Link to={href} className={combinedClassName}>
        {children}
      </Link>
    );
  }

  return (
    <button className={combinedClassName} {...props}>
      {children}
    </button>
  );
};
